import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  activateMerchant,
  getMerchant,
  rotateCredentials,
  suspendMerchant,
  type Merchant,
  type MerchantWithCredentials,
} from "@/lib/api";
import { formatDate } from "@/lib/format";
import { ArrowLeft, RotateCcw, Pause, Play, Copy, CheckCircle2, AlertTriangle } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Badge, statusColor } from "@/components/DataTable";

export function MerchantDetailPage() {
  const { id: merchantId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [rotated, setRotated] = useState<MerchantWithCredentials | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const load = async () => {
    if (!merchantId) return;
    setLoading(true);
    try {
      setMerchant(await getMerchant(merchantId));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId]);

  const copy = async (key: string, value: string) => {
    await navigator.clipboard.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const onRotate = async () => {
    if (!merchantId) return;
    if (!confirm("Régénérer les credentials ? L'ancienne clé sera révoquée.")) return;
    setRotating(true);
    try {
      const result = await rotateCredentials(merchantId);
      setRotated(result);
    } finally {
      setRotating(false);
    }
  };

  const onSuspend = async () => {
    if (!merchantId || !merchant) return;
    if (!confirm(`Suspendre ${merchant.business_name} ?`)) return;
    await suspendMerchant(merchantId);
    await load();
  };

  const onActivate = async () => {
    if (!merchantId) return;
    await activateMerchant(merchantId);
    await load();
  };

  if (loading) return <div className="card card-body text-[#7e8299]">Chargement…</div>;
  if (!merchant) return <div className="card card-body text-danger-700">Marchand introuvable</div>;

  return (
    <div>
      <PageHeader
        title={merchant.business_name}
        breadcrumbs={[
          { label: "Administration" },
          { label: "Marchands", to: "/admin/merchants" },
          { label: merchant.business_name },
        ]}
        actions={
          <>
            <button onClick={() => navigate("/admin/merchants")} className="btn btn-light btn-sm">
              <ArrowLeft size={14} />
              Retour
            </button>
            <button onClick={onRotate} disabled={rotating} className="btn btn-light-primary btn-sm">
              <RotateCcw size={14} />
              {rotating ? "..." : "Rotation credentials"}
            </button>
            {merchant.status === "active" ? (
              <button onClick={onSuspend} className="btn btn-light-danger btn-sm">
                <Pause size={14} />
                Suspendre
              </button>
            ) : (
              <button onClick={onActivate} className="btn btn-success btn-sm">
                <Play size={14} />
                Réactiver
              </button>
            )}
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
        <div className="card lg:col-span-2">
          <div className="card-header">
            <div className="card-title">Informations</div>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
              <Field label="ID" value={<span className="font-mono text-xs">{merchant.id}</span>} />
              <Field label="Email" value={merchant.business_email} />
              <Field label="Téléphone" value={merchant.business_phone ?? "—"} />
              <Field label="Pays" value={merchant.country} />
              <Field
                label="Statut"
                value={<Badge color={statusColor(merchant.status)}>{merchant.status}</Badge>}
              />
              <Field
                label="Mode"
                value={<Badge color={merchant.livemode ? "info" : "light"}>{merchant.livemode ? "Live" : "Test"}</Badge>}
              />
              <Field label="Créé le" value={formatDate(merchant.created_at)} />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-title">Avatar</div>
          </div>
          <div className="card-body flex flex-col items-center justify-center py-8">
            <div className="w-24 h-24 rounded-2xl bg-brand-50 text-brand-700 flex items-center justify-center font-bold text-4xl">
              {merchant.business_name.slice(0, 2).toUpperCase()}
            </div>
            <div className="mt-4 text-center">
              <div className="font-bold text-[#181c32]">{merchant.business_name}</div>
              <div className="text-xs text-[#7e8299] mt-1">{merchant.business_email}</div>
            </div>
          </div>
        </div>
      </div>

      {rotated && (
        <div className="card mb-6">
          <div className="card-header bg-warning-50">
            <div className="flex items-center gap-3">
              <AlertTriangle size={22} className="text-warning-700" />
              <div>
                <div className="card-title text-warning-700">Nouveaux credentials générés</div>
                <div className="card-subtitle">Affichés une seule fois — copiez-les maintenant</div>
              </div>
            </div>
          </div>
          <div className="card-body space-y-4">
            {[
              { key: "api_key", label: "API Key", value: rotated.api_key },
              { key: "api_secret", label: "API Secret", value: rotated.api_secret },
              { key: "hmac_callback_secret", label: "HMAC Callback Secret", value: rotated.hmac_callback_secret },
            ].map((c) => (
              <div key={c.key}>
                <div className="form-label">{c.label}</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-4 py-2.5 bg-[#f5f8fa] rounded-lg text-sm font-mono break-all">{c.value}</code>
                  <button onClick={() => copy(c.key, c.value)} className="btn btn-light-primary btn-sm">
                    {copied === c.key ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                    {copied === c.key ? "Copié" : "Copier"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-[#7e8299] font-bold">{label}</div>
      <div className="mt-1.5 text-[#181c32] text-sm">{value}</div>
    </div>
  );
}
